<template>
  <div class="p-5 pt-1">
    <p class="font-semibold text-3xl">
      List Monitoring Report
    </p>
    <div class="mt-6">
      <div class="bg-white rounded-2xl p-6">
        <div class="flex flex-wrap flex-row items-center justify-start">
          <span class="font-semibold text-lg mr-5">Filter</span>
          <select-with-search
            class="flex-grow"
            :options="options.company_name"
            placeholder="Select Company"
            v-model="filter.company_name"
          />
          <select-with-search
            class="flex-grow ml-6"
            :options="options.tugboat_name"
            placeholder="Select tug boat"
            v-model="filter.tugboat_name"
          />
          <date-picker
            class="flex-grow ml-6"
            type="date"
            value-type="YYYY-MM-DD"
            format="DD-MMM-YYYY"
            placeholder="Date Start"
            v-model="filter.start_date"
            :disabled-date="disabledStartDate"
          />
          <span class="mx-3">-</span>
          <date-picker
            class="flex-grow"
            type="date"
            value-type="YYYY-MM-DD"
            format="DD-MMM-YYYY"
            placeholder="Date End"
            v-model="filter.end_date"
            :disabled-date="disabledEndDate"
          />
          <vs-button
            class="button shadow-secondary ml-6"
            color="secondary"
            @click="fetchMonitoringReports"
          >
            Filter
          </vs-button>
        </div>
      </div>
    </div>
    <table-top
      :page-size-start="false"
      class="mt-6"
      @fetch="fetchMonitoringReports()"
    >
      <template #start>
        <vs-button
          ref="downloadButton"
          class="button shadow-secondary font-semibold vs-con-loading__container"
          color="secondary"
          :disabled="!monitoringReportData.length"
          @click="downloadMonitoringReports"
        >
          Export Excel
        </vs-button>
      </template>
    </table-top>
    <data-table
      class="mt-6"
      :loading="isBusy"
      :table-header="monitoringReportHeaders"
      :table-data="monitoringReportData"
      :sort-by="filter.sort_by"
      :sort-dir="filter.sort_dir"
      body-height="100vh"
      @sort="sort"
    >
      <template #position="{ row }">
        <div class="flex flex-col">
          <span>Lat: {{ row.latitude }}</span>
          <span>Long: {{ row.longitude }}</span>
        </div>
      </template>
      <template #heading="{ row }">
        {{ row.heading }} degree
      </template>
      <template #speed="{ row }">
        {{ row.speed }} knots
      </template>
      <template #position_time="{ row }">
        {{ moment(row.position_time).format('DD MMM YYYY HH:mm:ss') }}
      </template>
    </data-table>
    <table-bottom
      class="mt-6"
      @fetch="fetchMonitoringReports()"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import SelectWithSearch from '@/components/selectWithSearch'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'
import downloadUtils from '@/utils/downloadUtils'

export default {
  metaInfo: {
    title: `${PAGE_TITLE} List Monitoring Report`
  },
  components: {
    DatePicker,
    SelectWithSearch,
    TableBottom,
    TableTop,
    DataTable
  },
  data() {
    return {
      isDownload: false,
      isBusy: false,
      options: {
        company_name: [],
        tugboat_name: []
      },
      filter: {
        company_name: '',
        tugboat_name: '',
        start_date: '',
        end_date: '',
        sort_by: '',
        sort_dir: ''
      },
      monitoringReportHeaders: [
        { key: 'tugboat_name', label: 'Tugboat', sortable: true },
        { key: 'barge_name', label: 'Barge Name', sortable: true },
        { key: 'trip_number', label: 'Service Trip', sortable: true },
        { key: 'contract_type', label: 'Contract Type', sortable: true },
        { key: 'position', label: 'Position', sortable: true },
        { key: 'heading', label: 'Heading', sortable: true },
        { key: 'speed', label: 'Speed', sortable: true },
        { key: 'proximity', label: 'Proximity', sortable: true },
        { key: 'position_time', label: 'Last Position Time', sortable: true }
      ],
      monitoringReportData: []
    }
  },
  computed: {
    ...mapGetters({
      monitoringReports: 'MonitoringReport/monitoringReports',
      monitoringReportFile: 'MonitoringReport/monitoringReportFile',
      companies: 'MonitoringReport/companies',
      tugsNames: 'MonitoringReport/tugsNames'
    })
  },
  watch: {
    companies: {
      handler(val) {
        this.options.company_name = val.map(item => ({ value: item.name, text: item.name }))
      }
    },
    tugsNames: {
      handler(val) {
        this.options.tugboat_name = val.map(item => ({ value: item.tugboat_name, text: item.tugboat_name }))
      }
    },
    monitoringReports: {
      handler(val) {
        this.monitoringReportData = val
      }
    },
    monitoringReportFile: {
      handler(val) {
        if (val) {
          downloadUtils(val, `monitoring_report_${this.moment().format('DD_MMM_YYYY')}`)
          this.$store.dispatch('MonitoringReport/resetFile')
        }
      }
    },
    'filter.company_name': {
      handler(val) {
        this.fetchTugNames({ company_name: val })
        this.filter.tugboat_name = ''
      }
    }
  },
  mounted() {
    this.fetchCompanies()
  },
  methods: {
    async fetchCompanies() {
      await this.$store.dispatch('MonitoringReport/fetchCompanies')
    },
    async fetchTugNames(filter) {
      await this.$store.dispatch('MonitoringReport/fetchTugNames', filter)
    },
    async fetchMonitoringReports() {
      try {
        this.isBusy = true
        const filter = {
          ...this.filter,
          start_date: this.filter.start_date
            ? this.moment(this.filter.start_date, ['YYYY-MM-DD']).format('YYYY-MM-DDT00:00:00.000Z')
            : this.filter.start_date,
          end_date: this.filter.end_date
            ? this.moment(this.filter.end_date, ['YYYY-MM-DD']).format('YYYY-MM-DDT23:59:59.000Z')
            : this.filter.end_date,
          sort_dir: this.filter.sort_dir || 'desc'
        }

        await this.$store.dispatch('MonitoringReport/fetchMonitoringReports',
          Object.keys(filter)
            .filter(key => !!filter[key])
            .reduce((item, key) => {
              item[key] = filter[key]
              return item
            }, {})
        )
        this.isBusy = false
      } catch (e) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch Monitoring Reports list, please try again')
      }
    },
    async downloadMonitoringReports() {
      try {
        this.isBusy = true
        const filter = {
          ...this.filter,
          start_date: this.filter.start_date
            ? this.moment(this.filter.start_date, ['YYYY-MM-DD']).format('YYYY-MM-DDT00:00:00.000Z')
            : this.filter.start_date,
          end_date: this.filter.end_date
            ? this.moment(this.filter.end_date, ['YYYY-MM-DD']).format('YYYY-MM-DDT23:59:59.000Z')
            : this.filter.end_date,
          sort_dir: this.filter.sort_dir || 'desc'
        }

        this.isDownload = true
        this.changeButtonState(this.isDownload, this.$refs.downloadButton.$el)

        await this.$store.dispatch('MonitoringReport/downloadMonitoringReports',
          Object.keys(filter)
            .filter(key => !!filter[key])
            .reduce((item, key) => {
              item[key] = filter[key]
              return item
            }, {})
        )
        this.isBusy = false
        this.isDownload = false
        this.changeButtonState(this.isDownload, this.$refs.downloadButton.$el)
      } catch (e) {
        this.isBusy = false
        this.isDownload = false
        this.changeButtonState(this.isDownload, this.$refs.downloadButton.$el)
        this.showFailedNotification(this, 'Failed to download Monitoring Reports list, please try again')
      }
    },
    sort(sortBy, sortDir) {
      this.filter.sort_by = sortBy
      this.filter.sort_dir = sortDir

      this.fetchMonitoringReports()
    },
    disabledStartDate(date) {
      return date < this.moment().subtract(1, 'months')
    },
    disabledEndDate(date) {
      return date > this.moment().add(1, 'months')
    },
    changeButtonState(state, container) {
      if (state) {
        this.$vs.loading({
          background: 'secondary',
          color: '#FFF',
          container,
          scale: 0.35
        })
        return
      }

      this.$vs.loading.close(container)
    }
  }
}
</script>

<style lang="scss" scoped>
.button {
  @apply py-4 #{!important};
  @apply rounded-lg;
  @apply border-2;
  @apply font-semibold;
  @apply min-w-button;
  @apply text-base;
  height: calc( 1rem * 10 / 3 );

  &.shadow-secondary {
    -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
    box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
  }

  &:focus {
    @apply outline-none;
  }
}

/deep/.select-container {
  @apply w-form-field #{!important};
}

/deep/.bg-option-container {
  width: unset !important;
  min-width: 100% !important;
}

/deep/.bg-accent-multiselect {
  span {
    @apply truncate;
  }
}

/deep/.bg-inner-option-container {
  ul {
    li {
      span {
        white-space: nowrap;
      }
    }
  }
}
</style>
